// Real QR encoding for printed labels (qrcode-generator: byte mode, level L).
//
// Deterministic: the same text always yields the same modules and the same SVG
// string, so the preview and the printed document carry identical codes. The
// SVG is a single path of horizontal runs, scaled by the caller through CSS
// width/height; `shape-rendering: crispEdges` keeps module edges sharp.

import qrcode from "qrcode-generator";

// Quiet zone drawn inside the SVG. The standard asks for 4 modules; the label's
// own white column gap supplies the rest, so the clear margin is never smaller.
export const QR_QUIET_ZONE_MODULES = 2;
// Below this printed module size, handheld scanners stop being reliable.
export const QR_MIN_MODULE_MM = 0.28;

const cache = new Map();

/** `{ ok: true, svg, size, modules }` (size = data modules, modules = incl. quiet zone) or `{ ok: false }`. */
export const renderQrSvg = (text) => {
  const value = String(text ?? "").trim();
  if (!value) return { ok: false };
  if (cache.has(value)) return cache.get(value);

  let result;
  try {
    const code = qrcode(0, "L");
    code.addData(value);
    code.make();

    const size = code.getModuleCount();
    const modules = size + QR_QUIET_ZONE_MODULES * 2;
    let path = "";
    for (let row = 0; row < size; row += 1) {
      let column = 0;
      while (column < size) {
        if (!code.isDark(row, column)) {
          column += 1;
          continue;
        }
        let end = column;
        while (end < size && code.isDark(row, end)) end += 1;
        path += `M${column + QR_QUIET_ZONE_MODULES} ${row + QR_QUIET_ZONE_MODULES}h${end - column}v1h-${end - column}z`;
        column = end;
      }
    }

    result = {
      ok: true,
      size,
      modules,
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${modules} ${modules}" shape-rendering="crispEdges" role="img" aria-label="QR"><path fill="#000" d="${path}"/></svg>`,
    };
  } catch (_error) {
    result = { ok: false };
  }

  cache.set(value, result);
  return result;
};

/** Printed width of one module when the whole code (quiet zone included) is `boxMM` wide. */
export const getQrModuleMM = (boxMM, modules) => (modules > 0 ? boxMM / modules : 0);
